import { motion } from "framer-motion";
import { Phone, WhatsappLogo, Medal } from "@phosphor-icons/react";
import { COMPANY, waLink } from "@/data/company";

export default function Leadership() {
  return (
    <section
      id="leadership"
      className="py-20 lg:py-32 bg-[#0B1120] relative overflow-hidden"
      data-testid="leadership-section"
    >
      {/* Gold grid accent */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#F59E0B 1px, transparent 1px), linear-gradient(90deg, #F59E0B 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-[2px] bg-[#F59E0B]" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#F59E0B]">
              Our Leadership
            </span>
          </div>
          <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            The people behind
            <br />
            <span className="text-gold-gradient">every delivery.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-white/70 text-base lg:text-lg font-medium leading-relaxed">
            Shree Ambey Roadlines is led by the Shukla family — three generations
            personally accountable for every consignment that moves under our
            name.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-5xl">
          {COMPANY.owners.map((o, i) => (
            <motion.div
              key={o.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              data-testid={`leader-card-${o.name.split(" ")[0].toLowerCase()}`}
              className="group border-2 border-[#F59E0B]/40 bg-white/[0.03] backdrop-blur-sm overflow-hidden shadow-brutal-gold hover:border-[#F59E0B] transition-brutal"
            >
              <div className="relative h-96 sm:h-[420px] overflow-hidden">
                <img
                  src={o.photo}
                  alt={o.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-transparent to-transparent"
                  aria-hidden
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0B1120]/80 backdrop-blur border border-[#F59E0B]/50 px-3 py-1.5">
                  <Medal size={14} weight="fill" className="text-[#F59E0B]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F59E0B]">
                    {i === 0 ? "Founder" : "Partner"}
                  </span>
                </div>
              </div>

              <div className="p-6 lg:p-8 border-t-2 border-[#F59E0B]/40">
                <h3 className="font-display font-black text-white text-2xl lg:text-3xl tracking-tight">
                  {o.name}
                </h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">
                  {o.role}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <a
                    href={`tel:${o.phone}`}
                    data-testid={`leader-call-${o.name.split(" ")[0].toLowerCase()}`}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-3 bg-white text-slate-900 border-2 border-white hover:bg-transparent hover:text-white transition-brutal"
                  >
                    <Phone size={14} weight="bold" />
                    {o.phone}
                  </a>
                  <a
                    href={waLink(o)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`leader-wa-${o.name.split(" ")[0].toLowerCase()}`}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-3 bg-[#25D366] text-white border-2 border-[#25D366] hover:bg-[#128C7E] hover:border-[#128C7E] transition-brutal"
                    aria-label={`WhatsApp ${o.name}`}
                  >
                    <WhatsappLogo size={16} weight="bold" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
